import { supabase } from '../lib/supabase'

export interface DynamicLocationData {
  slug: string
  name: string
  arabicName?: string
  city: string
}

// Cache for dynamic locations to avoid repeated database calls
let locationCache: Record<string, DynamicLocationData> = {}
let cacheTimestamp: number = 0
const CACHE_DURATION = 5 * 60 * 1000 // 5 minutes

/**
 * Generate a URL-safe slug from city name
 */
export function generateCitySlug(cityName: string): string {
  // Special mappings for cities with known issues
  const specialMappings: Record<string, string> = {
    'Meknés': 'meknes',
    'Meknes': 'meknes',
    'Fès': 'fes',
    'Fes': 'fes',
    'Tanger': 'tanger',
    'Tétouan': 'tetouan',
    'Tetouan': 'tetouan',
    'Agadir': 'agadir',
    'Casablanca': 'casablanca',
    'Rabat': 'rabat',
    'Marrakech': 'marrakech',
    'Oujda': 'oujda',
    'Safi': 'safi',
    'Kenitra': 'kenitra'
  }
  
  // Check special mappings first
  if (specialMappings[cityName]) {
    return specialMappings[cityName];
  }
  
  // For other cities, use intelligent slug generation
  return cityName
    .toLowerCase()
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove accents
    .replace(/[^a-z0-9\u0600-\u06FF]+/g, '-') // Replace spaces and special chars with hyphens (preserve Arabic)
    .replace(/-+/g, '-') // Remove multiple consecutive hyphens
    .replace(/^-|-$/g, '') // Remove leading/trailing hyphens
}

/**
 * Convert slug back to city name
 */
export function slugToCityName(slug: string): string {
  // Special reverse mappings for known city slugs
  const reverseMappings: Record<string, string> = {
    'meknes': 'Meknès',
    'fes': 'Fès',
    'tanger': 'Tanger',
    'tetouan': 'Tétouan',
    'agadir': 'Agadir',
    'casablanca': 'Casablanca',
    'rabat': 'Rabat',
    'marrakech': 'Marrakech',
    'oujda': 'Oujda',
    'safi': 'Safi',
    'kenitra': 'Kenitra'
  }
  
  // Check reverse mappings first
  if (reverseMappings[slug]) {
    return reverseMappings[slug];
  }
  
  // For other slugs, use standard conversion
  return slug
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

/**
 * Fetch all active cities from database and generate dynamic location data
 */
export async function fetchDynamicLocations(): Promise<Record<string, DynamicLocationData>> {
  const now = Date.now()
  
  // Return cached data if still valid
  if (cacheTimestamp && (now - cacheTimestamp) < CACHE_DURATION && Object.keys(locationCache).length > 0) {
    return locationCache
  }

  try {
    const { data: cities, error } = await supabase
      .from('agency_locations')
      .select('city')
      .eq('is_active', true)
      .order('city')

    if (error) {
      console.error('Error fetching cities:', error)
      return locationCache // Return cached data on error
    }

    // Get unique cities
    const uniqueCities = [...new Set(cities?.map(c => c.city) || [])]
    
    // Generate location data for each city
    const newLocationCache: Record<string, DynamicLocationData> = {}
    
    uniqueCities.forEach(city => {
      const slug = generateCitySlug(city)
      newLocationCache[slug] = {
        slug,
        name: city,
        city,
        // You can add Arabic names here if needed
        arabicName: getArabicCityName(city)
      }
    })

    // Update cache
    locationCache = newLocationCache
    cacheTimestamp = now

    console.log('🏙️ Dynamic locations loaded:', Object.keys(locationCache).length, 'cities')
    
    return locationCache
  } catch (error) {
    console.error('Error in fetchDynamicLocations:', error)
    return locationCache // Return cached data on error
  }
}

/**
 * Get location data by slug (checks both static and dynamic)
 */
export async function getLocationBySlug(slug: string): Promise<DynamicLocationData | null> {
  // First check static locations (for backward compatibility)
  const staticLocations = await import('../utils/seoUrls')
  if (staticLocations.locationSlugs[slug]) {
    const staticLoc = staticLocations.locationSlugs[slug]
    return {
      slug: staticLoc.slug,
      name: staticLoc.name,
      city: staticLoc.name,
      arabicName: staticLoc.arabicName
    }
  }

  // Then check dynamic locations
  const dynamicLocations = await fetchDynamicLocations()
  return dynamicLocations[slug] || null
}

/**
 * Get all locations (static + dynamic)
 */
export async function getAllLocations(): Promise<Record<string, DynamicLocationData>> {
  const dynamicLocations = await fetchDynamicLocations()
  
  // Merge with static locations for backward compatibility
  const staticLocations = await import('../utils/seoUrls')
  const allLocations: Record<string, DynamicLocationData> = { ...dynamicLocations }
  
  // Add static locations (they take precedence over dynamic ones)
  Object.entries(staticLocations.locationSlugs).forEach(([slug, data]) => {
    allLocations[slug] = {
      slug: data.slug,
      name: data.name,
      city: data.name,
      arabicName: data.arabicName
    }
  })
  
  return allLocations
}

/**
 * Find location by city name (fuzzy matching)
 */
export async function findLocationByCity(cityName: string): Promise<DynamicLocationData | null> {
  const allLocations = await getAllLocations()
  
  // Normalize the search term
  const normalizedSearch = cityName.toLowerCase().trim()
  
  // Try exact match first
  const exactMatch = Object.values(allLocations).find(loc => 
    loc.name.toLowerCase() === normalizedSearch ||
    loc.city.toLowerCase() === normalizedSearch
  )
  if (exactMatch) return exactMatch
  
  // Try partial match
  const partialMatch = Object.values(allLocations).find(loc => 
    loc.name.toLowerCase().includes(normalizedSearch) ||
    normalizedSearch.includes(loc.name.toLowerCase())
  )
  if (partialMatch) return partialMatch
  
  // Generate dynamic location for unknown cities
  const slug = generateCitySlug(cityName)
  return {
    slug,
    name: cityName,
    city: cityName,
    arabicName: getArabicCityName(cityName)
  }
}

/**
 * Get Arabic name for cities (you can expand this mapping)
 */
function getArabicCityName(cityName: string): string | undefined {
  const arabicNames: Record<string, string> = {
    'Casablanca': 'الدار البيضاء',
    'Rabat': 'الرباط',
    'Marrakech': 'مراكش',
    'Fès': 'فاس',
    'Tanger': 'طنجة',
    'Agadir': 'أكادير',
    'Meknès': 'مكناس',
    'Oujda': 'وجدة',
    'Fquih Ben Salah': 'فقيه بن صالح',
    'Er-Rich': 'الريش',
    'Tétouan': 'تطوان',
    'Safi': 'آسفي',
    'Kenitra': 'القنيطرة'
  }
  
  return arabicNames[cityName]
}

/**
 * Clear the location cache (useful for testing or when data changes)
 */
export function clearLocationCache(): void {
  locationCache = {}
  cacheTimestamp = 0
}
